/**
 * Auth V3 — persisted device session metadata.
 *
 * Stores the non-secret pairing result (businessDeviceId, businessId, terminal id,
 * etc.) so the app knows its device identity across restarts. The private key itself
 * lives in IndexedDB (device-identity.ts), never here.
 */

import { type BusinessDeviceType, BusinessDeviceTypeSchema } from '@am3nd-org/pointcraft-types';

import {
  SECURE_STORAGE_KEYS,
  secureStorageService,
  type SecureStorageReadResult,
} from '../secure-storage.service';

// ----------------------------------------------------------------------

export interface StoredDeviceSessionV3 {
  businessId: string;
  businessDeviceId: string;
  firebaseUid?: string;
  businessLocationId?: string;
  paymentTerminalId?: string;
  deviceType: BusinessDeviceType;
  deviceFingerprint: string;
  /** SHA-256 fingerprint of the signing public key. Added for cache-loss recovery. */
  publicKeyFingerprint?: string;
  pairedAt: string; // ISO string, stamped by caller
}

export interface StoredDeviceMigrationStateV3 {
  status: 'idle' | 'in_progress' | 'retry_wait' | 'completed' | 'failed_terminal';
  legacyFirebaseUid?: string;
  attemptCount: number;
  startedAt: string;
  attemptedAt?: string;
  completedAt?: string;
  lastErrorClassification?: string;
  nextRetryAt?: string;
  supportReference?: string;
}

export interface StoredLegacyDeviceTerminalHandoffV3 {
  businessId: string;
  businessLocationId: string;
  deviceName: string;
  capturedAt: string;
}

/** What the underlying storage read itself reported. */
export type DeviceSessionReadStatus = 'ok' | 'absent' | 'unreadable' | 'invalid';

export interface DeviceSessionReadResult {
  session: StoredDeviceSessionV3 | null;
  /** 'unreadable' is kept even when the session is served from the memory cache. */
  status: DeviceSessionReadStatus;
  /** True when the session came from the in-memory last-known-good copy. */
  fromCache: boolean;
  /** Where the raw record was read from, when one was read this call. */
  source?: 'native' | 'local-fallback';
}

/**
 * Last-known-good copy of the parsed session. Secure-storage reads are live
 * platform I/O and can fail transiently (iOS Keychain while locked/waking);
 * without this, a single failed read reports a paired register as unpaired.
 * Consulted ONLY for 'unreadable' reads — a clean "not found" ('absent') must
 * never be resurrected from memory. The only out-of-band clear of the
 * underlying keys is logout cleanup, which calls invalidateDeviceSessionCache.
 */
let lastKnownGoodSession: { session: StoredDeviceSessionV3; slot: 'primary' | 'pending' } | null =
  null;

let deviceSessionGeneration = 0;

// Secure storage has no compare-and-swap primitive. Serialize mutations so a
// superseded repair can remove its own late value before a newer pairing is
// allowed to persist. Generation changes remain synchronous, so queued work is
// invalidated as soon as logout/re-pairing begins.
let sessionMutationBarrier: Promise<void> = Promise.resolve();

function serializeSessionMutation<T>(mutation: () => Promise<T>): Promise<T> {
  const result = sessionMutationBarrier.then(mutation, mutation);
  sessionMutationBarrier = result.then(
    () => undefined,
    () => undefined
  );
  return result;
}

export class DeviceSessionGenerationChangedError extends Error {
  constructor() {
    super('The device session changed while it was being saved');
    this.name = 'DeviceSessionGenerationChangedError';
  }
}

export function getDeviceSessionGeneration(): number {
  return deviceSessionGeneration;
}

export function invalidateDeviceSessionCache(): void {
  deviceSessionGeneration += 1;
  lastKnownGoodSession = null;
}

export async function saveDeviceSession(
  session: StoredDeviceSessionV3,
  options: { expectedGeneration?: number } = {}
): Promise<void> {
  if (
    options.expectedGeneration !== undefined &&
    options.expectedGeneration !== deviceSessionGeneration
  ) {
    throw new DeviceSessionGenerationChangedError();
  }
  const serializedSession = JSON.stringify(session);
  const writeGeneration = options.expectedGeneration ?? deviceSessionGeneration + 1;
  if (options.expectedGeneration === undefined) {
    deviceSessionGeneration = writeGeneration;
    lastKnownGoodSession = null;
  }
  await serializeSessionMutation(async () => {
    if (writeGeneration !== deviceSessionGeneration) {
      throw new DeviceSessionGenerationChangedError();
    }
    await secureStorageService.set(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3, serializedSession, {
      policy: 'device-session-metadata',
    });
    if (writeGeneration !== deviceSessionGeneration) {
      // A newer mutation is waiting behind this one. Remove this superseded
      // value while still holding the barrier, then let the newer write run.
      const current = await secureStorageService.get(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3);
      if (current === serializedSession) {
        await secureStorageService.remove(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3);
      }
      throw new DeviceSessionGenerationChangedError();
    }
    lastKnownGoodSession = { session, slot: 'primary' };
  });
}

export async function savePendingDeviceSession(session: StoredDeviceSessionV3): Promise<void> {
  const writeGeneration = deviceSessionGeneration + 1;
  deviceSessionGeneration = writeGeneration;
  lastKnownGoodSession = null;
  const serializedSession = JSON.stringify(session);
  await serializeSessionMutation(async () => {
    if (writeGeneration !== deviceSessionGeneration) {
      throw new DeviceSessionGenerationChangedError();
    }
    await secureStorageService.set(
      SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING,
      serializedSession,
      { policy: 'device-session-metadata' }
    );
    if (writeGeneration !== deviceSessionGeneration) {
      const current = await secureStorageService.get(
        SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING
      );
      if (current === serializedSession) {
        await secureStorageService.remove(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING);
      }
      throw new DeviceSessionGenerationChangedError();
    }
    lastKnownGoodSession = { session, slot: 'pending' };
  });
}

function parseDeviceSession(raw: string | null): StoredDeviceSessionV3 | null {
  if (!raw) return null;
  try {
    const session = JSON.parse(raw) as Partial<StoredDeviceSessionV3>;
    if (
      typeof session.businessId !== 'string' ||
      !session.businessId.trim() ||
      typeof session.businessDeviceId !== 'string' ||
      !session.businessDeviceId.trim() ||
      !BusinessDeviceTypeSchema.safeParse(session.deviceType).success ||
      typeof session.deviceFingerprint !== 'string' ||
      !session.deviceFingerprint.trim() ||
      typeof session.pairedAt !== 'string' ||
      !Number.isFinite(Date.parse(session.pairedAt)) ||
      (session.firebaseUid !== undefined &&
        (typeof session.firebaseUid !== 'string' || !session.firebaseUid.trim())) ||
      (session.businessLocationId !== undefined &&
        (typeof session.businessLocationId !== 'string' || !session.businessLocationId.trim())) ||
      (session.paymentTerminalId !== undefined &&
        (typeof session.paymentTerminalId !== 'string' || !session.paymentTerminalId.trim())) ||
      (session.publicKeyFingerprint !== undefined &&
        (typeof session.publicKeyFingerprint !== 'string' || !session.publicKeyFingerprint.trim()))
    ) {
      return null;
    }
    return session as StoredDeviceSessionV3;
  } catch {
    return null;
  }
}

/**
 * A native read failure is transient and may consult memory. A present record
 * that no longer parses is invalid and must never be replaced by cached data.
 */
function isInvalidRead(read: SecureStorageReadResult, parsed: StoredDeviceSessionV3 | null) {
  return read.value !== null && !parsed;
}

export async function loadDeviceSessionDetailed(): Promise<DeviceSessionReadResult> {
  const primary = await secureStorageService.getWithDiagnostics(
    SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3
  );
  const primarySession = parseDeviceSession(primary.value);
  if (primarySession) {
    lastKnownGoodSession = { session: primarySession, slot: 'primary' };
    return {
      session: primarySession,
      status: 'ok',
      fromCache: false,
      source: primary.source === 'native' ? 'native' : 'local-fallback',
    };
  }

  const pending = await secureStorageService.getWithDiagnostics(
    SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING
  );
  const pendingSession = parseDeviceSession(pending.value);
  if (pendingSession) {
    lastKnownGoodSession = { session: pendingSession, slot: 'pending' };
    return {
      session: pendingSession,
      status: 'ok',
      fromCache: false,
      source: pending.source === 'native' ? 'native' : 'local-fallback',
    };
  }

  if (isInvalidRead(primary, primarySession) || isInvalidRead(pending, pendingSession)) {
    return { session: null, status: 'invalid', fromCache: false };
  }

  if (primary.nativeReadFailed || pending.nativeReadFailed) {
    if (lastKnownGoodSession) {
      return { session: lastKnownGoodSession.session, status: 'unreadable', fromCache: true };
    }
    return { session: null, status: 'unreadable', fromCache: false };
  }

  return { session: null, status: 'absent', fromCache: false };
}

export async function loadDeviceSession(): Promise<StoredDeviceSessionV3 | null> {
  return (await loadDeviceSessionDetailed()).session;
}

export async function clearDeviceSession(): Promise<void> {
  invalidateDeviceSessionCache();
  await serializeSessionMutation(async () => {
    await Promise.all([
      secureStorageService.remove(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3),
      secureStorageService.remove(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING),
    ]);
  });
}

export async function promotePendingDeviceSession(session: StoredDeviceSessionV3): Promise<void> {
  await saveDeviceSession(session);
  const promotedGeneration = deviceSessionGeneration;
  await serializeSessionMutation(async () => {
    // A newer pairing/pending write owns the slot if it advanced generation
    // before this cleanup reached the mutation barrier.
    if (promotedGeneration !== deviceSessionGeneration) return;
    await secureStorageService.remove(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING);
  });
}

export async function clearPendingDeviceSession(): Promise<void> {
  deviceSessionGeneration += 1;
  if (lastKnownGoodSession?.slot === 'pending') lastKnownGoodSession = null;
  await serializeSessionMutation(() =>
    secureStorageService.remove(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_PENDING)
  );
}

export async function saveDeviceMigrationState(state: StoredDeviceMigrationStateV3): Promise<void> {
  await secureStorageService.set(
    SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_MIGRATION_STATE,
    JSON.stringify(state),
    { policy: 'device-session-metadata' }
  );
}

export async function loadDeviceMigrationState(): Promise<StoredDeviceMigrationStateV3 | null> {
  const raw = await secureStorageService.get(
    SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_MIGRATION_STATE
  );
  if (!raw) return null;
  try {
    const state = JSON.parse(raw) as Partial<StoredDeviceMigrationStateV3>;
    const validStatuses: StoredDeviceMigrationStateV3['status'][] = [
      'idle',
      'in_progress',
      'retry_wait',
      'completed',
      'failed_terminal',
    ];
    if (
      !state.status ||
      !validStatuses.includes(state.status) ||
      typeof state.attemptCount !== 'number' ||
      !Number.isInteger(state.attemptCount) ||
      state.attemptCount < 0 ||
      typeof state.startedAt !== 'string'
    ) {
      return null;
    }
    return state as StoredDeviceMigrationStateV3;
  } catch {
    return null;
  }
}

export async function clearDeviceMigrationState(): Promise<void> {
  await secureStorageService.remove(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_MIGRATION_STATE);
}

export async function saveLegacyDeviceTerminalHandoff(
  handoff: StoredLegacyDeviceTerminalHandoffV3
): Promise<void> {
  await secureStorageService.set(
    SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_LEGACY_TERMINAL_HANDOFF,
    JSON.stringify(handoff),
    { policy: 'device-session-metadata' }
  );
}

export async function loadLegacyDeviceTerminalHandoff(): Promise<StoredLegacyDeviceTerminalHandoffV3 | null> {
  const raw = await secureStorageService.get(
    SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_LEGACY_TERMINAL_HANDOFF
  );
  if (!raw) return null;
  try {
    const handoff = JSON.parse(raw) as Partial<StoredLegacyDeviceTerminalHandoffV3>;
    if (
      typeof handoff.businessId !== 'string' ||
      typeof handoff.businessLocationId !== 'string' ||
      typeof handoff.deviceName !== 'string' ||
      typeof handoff.capturedAt !== 'string'
    ) {
      return null;
    }
    return handoff as StoredLegacyDeviceTerminalHandoffV3;
  } catch {
    return null;
  }
}

export async function clearLegacyDeviceTerminalHandoff(): Promise<void> {
  await secureStorageService.remove(SECURE_STORAGE_KEYS.BUSINESS_DEVICE_V3_LEGACY_TERMINAL_HANDOFF);
}
